import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  Renderer2,
} from '@angular/core';

import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MaxFileSize, validateMaxFileSize } from '@recruitee/utils';

import { AcceptedFileTypesService } from '../accepted-file-types/accepted-file-types.service';
import { FileTypesCategory } from '../accepted-file-types/accepted-file-types.types';

export enum FileReadStatus {
  OK,
  FILE_SIZE_EXCEEDED,
  FILE_EXTENSION_NOT_SUPPORTED,
}

export type FileOrFiles = File | FileList | null;

export interface FileReadResult {
  status: FileReadStatus;
  files: FileOrFiles;
}

@Directive({
  selector: '[rtFileHandler]',
  standalone: true,
  host: {
    '(click)': 'selfTrigger ? readFileFromDisk() : null',
  },
})
export class FileHandlerDirective implements AfterViewInit, OnDestroy {
  @Input('rtFileHandlerMultiple')
  public multiple: boolean;

  @Input('rtFileHandlerValidateMaxSize')
  public validateMaxSize: boolean;

  @Input('rtFileHandlerMaxSize')
  public maxSize: MaxFileSize = MaxFileSize.File50MB;

  @Input('rtFileHandlerSelfTrigger')
  public selfTrigger: boolean;

  @Input('rtFileHandlerAccept')
  public accept: FileTypesCategory;

  @Output()
  public rtFileHandlerNextFile: EventEmitter<FileReadResult> = new EventEmitter<FileReadResult>();

  private fileInput: HTMLInputElement;
  private ngOnDestroy$: Subject<void> = new Subject<void>();

  public get shouldValidate(): boolean {
    return this.validateMaxSize || !!this.accept;
  }

  constructor(
    private renderer: Renderer2,
    private el: ElementRef,
    private fileTypesService: AcceptedFileTypesService,
  ) {}

  public ngAfterViewInit(): void {
    const fileInput = this.renderer.createElement('input');
    this.renderer.setAttribute(fileInput, 'hidden', 'true');
    this.renderer.setAttribute(fileInput, 'type', 'file');

    if (this.multiple) {
      this.renderer.setAttribute(fileInput, 'multiple', 'true');
    }

    if (this.accept) {
      this.renderer.setAttribute(
        fileInput,
        'accept',
        this.fileTypesService.byCategoryAsString[this.accept],
      );
    }

    this.renderer.appendChild(this.el.nativeElement, fileInput);
    this.fileInput = fileInput;

    fromEvent(fileInput, 'change')
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe((event: any) => this.handleReadFile(event.target?.files ?? null));
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public readFileFromDisk(): void {
    this.fileInput.click();
  }

  private handleReadFile(files: FileList | null): void {
    if (!files) {
      this.rtFileHandlerNextFile.emit({
        status: FileReadStatus.OK,
        files: null,
      });
      return;
    }

    this.rtFileHandlerNextFile.emit({
      status: this.shouldValidate ? this.validateFiles(files) : FileReadStatus.OK,
      files: this.multiple ? files : files.item(0),
    });
    this.fileInput.value = '';
  }

  private validateFiles(files: FileList): FileReadStatus {
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i)!;

      if (this.isMaxSizeExceeded(file)) {
        return FileReadStatus.FILE_SIZE_EXCEEDED;
      } else if (
        this.fileTypesService.isInvalid(
          file,
          this.fileTypesService.byCategoryAsString?.[this.accept] ?? '',
        )
      ) {
        return FileReadStatus.FILE_EXTENSION_NOT_SUPPORTED;
      }
    }

    return FileReadStatus.OK;
  }

  private isMaxSizeExceeded(file: File): boolean {
    return !validateMaxFileSize(file, this.maxSize);
  }
}
